import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { AppState } from 'store';
import { getSelectedOrganization } from '../../utils/selectedOrganizationUtils';
import { signSetAction } from 'store/signup/actions';
import {
    DonateButton,
    DonateSection,
    FooterDescription,
    InfoWrapper,
    LabelContainer,
    OrgInfo,
    StyledButton,
    StyledLabel,
} from './style';
import { GivelifyCheckbox, GivelifyLabel } from '@givelify/ui';
import { PATH } from 'routes';
import { Link, useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import { useRouteConfig } from 'pages/signup/utils/useRouteConfig';
import { pushEvent } from 'utils/clevertapUtils';
import {
    CLICK_AUTHORITY_CHECKBOX,
    CLICK_GIVE_HERE,
    CLICK_ORGANIZATION_CONFIRM_BUTTON,
    CLICK_ORGANIZATION_INVITE,
    VIEW_EVENT,
} from 'utils/clevertapEvents';
import { useTranslation } from 'react-i18next';
import { addPhoneNumberFormatting } from 'store/signup/reducer';
import { webConfig } from 'webConfig';
import { Features } from 'store/featureFlags/types';
import { SEARCH_EXPERIMENT, SEARCH_UI_EXPERIMENT } from 'utils/clevertapVariantFlags';
import { useFullStoryIdentity } from 'pages/signup/utils/useFullStoryIdentity';

const ConfirmOrganizationInfo: React.FC = () => {
    const dispatch = useAppDispatch();
    const { fingerprint, fullStoryEnabled, selectedOrganization, country, newSearchUIEnabled, v2SearchAPIEnabled } = useSelector(
        (state: AppState) => ({
            fingerprint: state.Signup.fingerprint,
            fullStoryEnabled: state.FeatureFlagging.features[Features.FULLSTORY],
            selectedOrganization: state.Signup.selectedOrganization,
            country: state.Signup.organizationCountry,
            newSearchUIEnabled: state.FeatureFlagging.features[Features.SEARCH_UI_V2],
            v2SearchAPIEnabled: state.FeatureFlagging.features[Features.SEARCH_API_V2],
        }),
    );
    const { next } = useRouteConfig(PATH.SIGNUP.STEPS.SEARCH_ORGANIZATION);
    const push = useNavigate();
    const [checked, setChecked] = useState<boolean>(false);

    const { t } = useTranslation();

    useFullStoryIdentity({
        enabled: fullStoryEnabled && newSearchUIEnabled,
        fingerprint,
    });

    const copy = useMemo(
        () => ({
            heading: t('signup.organizationSearch.confirmPage.heading'),
            subHeading: t('signup.organizationSearch.confirmPage.subHeading'),
            checkbox: t('signup.organizationSearch.confirmPage.checkbox'),
            button: t('signup.organizationSearch.confirmPage.button'),
            notAuthorized: t(
                'signup.organizationSearch.confirmPage.notAuthorized',
            ),
            invite: t('signup.organizationSearch.confirmPage.invite'),
            donate: t('signup.organizationSearch.confirmPage.donate'),
            giveHere: t('signup.organizationSearch.confirmPage.giveHere'),
        }),
        [t],
    );

    useEffect(() => {
        if (!selectedOrganization) {
            const selected = getSelectedOrganization();

            if (!selected) {
                return;
            }

            dispatch(
                signSetAction({
                    selectedOrganization: selected,
                }),
            );
        }
    }, [selectedOrganization, dispatch]);

    useEffect(() => {
        pushEvent(VIEW_EVENT, {
            name: "ThisIsMyOrg",
            location: "SignupConfirmOrg"
        });
    }, []);

    const address = useMemo(() => {
        if (selectedOrganization) {
            const { address, city, state, zip } = selectedOrganization?.source;
            const countryName = country
                ? country === 'us'
                    ? ', USA'
                    : ', BAH'
                : '';
            return `${address}, ${city}, ${state} ${zip}${countryName}`;
        } else {
            return '';
        }
    }, [selectedOrganization, country]);

    const saveOrganization = useCallback(() => {
        if (!selectedOrganization) {
            return;
        }

        const { source } = selectedOrganization;

        dispatch(
            signSetAction({
                ypId: source.ypId,
                doneeId: Number(source.doneeId) || undefined,
                doneeType: source.doneeType,
                organizationName: source.name,
                organizationAddress: source.address,
                organizationPhoneNumber: source.phone,
                organizationCity: source.city,
                organizationState: source.state,
                organizationZip: source.zip,
                lat: source.lat,
                lon: source.lon,
            }),
        );
    }, [dispatch, selectedOrganization]);

    const onConfirm = useCallback(() => {
        saveOrganization();
        pushEvent(CLICK_ORGANIZATION_CONFIRM_BUTTON, {
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled ? 'on' : 'off' });
        next && push(next);
    }, [next, push, saveOrganization, v2SearchAPIEnabled, newSearchUIEnabled]);

    const onDonate = useCallback(() => {
        pushEvent(CLICK_GIVE_HERE, {
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled ? 'on' : 'off'});
        window.open(webConfig.givelifyDonorsUrl, '_blank');
    }, [newSearchUIEnabled, v2SearchAPIEnabled]);

    const onChange = (checked: boolean) => {
        pushEvent(CLICK_AUTHORITY_CHECKBOX, {
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled ? 'on' : 'off'});
        setChecked(checked);
    };

    const onInvite = () => {
        pushEvent(CLICK_ORGANIZATION_INVITE, {
            [SEARCH_EXPERIMENT]: v2SearchAPIEnabled ? 'on' : 'off',
            [SEARCH_UI_EXPERIMENT]: newSearchUIEnabled ? 'on' : 'off'});
        saveOrganization();
    };

    return (
        <>
            <InfoWrapper className="fs-unmask">
                <GivelifyLabel className="heading" text={copy.heading} />
                <GivelifyLabel
                    className="sub-heading"
                    text={copy.subHeading}
                    variant="body1"
                />

                <OrgInfo>
                    <GivelifyLabel
                        text={selectedOrganization?.source.name}
                        variant="body1B"
                    />
                    <GivelifyLabel
                        className="additional-info"
                        text={address}
                        variant="body2"
                    />
                    <GivelifyLabel
                        className="additional-info"
                        text={addPhoneNumberFormatting(
                            selectedOrganization?.source.phone,
                        )}
                        variant="body2"
                    />
                </OrgInfo>
                <LabelContainer>
                    <StyledLabel
                        control={
                            <GivelifyCheckbox
                                onChange={(_e, value) => onChange(value)}
                                name="authority-checkbox"
                            />
                        }
                        label={copy.checkbox}
                        className="checkbox"
                    />
                </LabelContainer>

                <StyledButton
                    text={copy.button}
                    variant="primary"
                    disabled={!checked}
                    size="large"
                    onClick={onConfirm}
                    name="my-organization"
                />

                <FooterDescription
                    className="footer-description"
                    text={copy.notAuthorized}
                />
                <Link
                    className="link"
                    to={PATH.SIGNUP.STEPS.INVITE_DONEE}
                    onClick={onInvite}
                    id="invite-link"
                >
                    {copy.invite}
                </Link>
            </InfoWrapper>
            <Grid
                className="fs-unmask"
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap={3}
                marginTop="40px"
            >
                <DonateSection container>
                    <GivelifyLabel className="secondary" text={copy.donate} />
                    <DonateButton
                        text={copy.giveHere}
                        variant="secondary"
                        size="large"
                        onClick={onDonate}
                        name="give-here"
                    />
                </DonateSection>
            </Grid>
        </>
    );
};

export default ConfirmOrganizationInfo;
